import React, { useEffect, useState, useMemo } from 'react';
import { Container, Row, Col, Collapse } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  AccessibilityNew as AccessibilityNewIcon,
  TipsAndUpdates as TipsAndUpdatesIcon,
  Bookmark as BookmarkIcon,
  AccessTimeFilled as AccessTimeFilledIcon,
  AccessAlarm as AccessAlarmIcon,
  TaskAlt as TaskAltIcon,
  PhonelinkErase as PhonelinkEraseIcon,
  ThumbUp as ThumbUpIcon,
  Cancel as CancelIcon,
  WorkHistory as WorkHistoryIcon,
  Backspace as BackspaceIcon,
  ShowChart as ShowChartIcon,
  PendingActions as PendingActionsIcon,
  Groups as GroupsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  AssignmentInd as AssignmentIndIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BarChart as BarChartIcon,
  Dashboard as DashboardIcon,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
} from 'chart.js';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { useTheme, useMediaQuery } from '@mui/material';
import { DashbordPerformanceForAllUsers } from './DashbordPerformanceForAllUsers';
import { Get_Dashboard_data_For_Mangers_crm_data } from '../Redux/Actions/UsersAction';
import { useSidebarWidth } from '../Utility/useSidebarWidth';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

/* ─── Design tokens — matches ManagerDashboard / SalesQuickNumberCRMData ──── */
const C = {
  bg: '#F7F8FC',
  white: '#FFFFFF',
  border: '#E8EAF0',
  accent: '#0D47A1',
  accentSoft: 'rgba(13,71,161,0.07)',
  text: '#1C2340',
  textMuted: '#7B829A',
  success: '#12B76A',
  warning: '#F79009',
  danger: '#F04438',
  shadow: '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd: '0 4px 16px rgba(16,24,40,0.10)',
};

const FONT = "'DM Sans', sans-serif";
const SIDEBAR_COLLAPSED = 64;

/* ─── Stage name display mapping ───────────────────────────────────────────
   نفس القاعدة المستخدمة في الداشبورد الرئيسية: مرحلة "No Stage" بتتعرض
   للمستخدم باسم "Fresh"، ومرحلة "Fresh" الأصلية (لو موجودة) بتتخفى خالص.   */
const getDisplayStageName = (name) => (name === 'No Stage' ? 'Fresh' : name);

/* ─── Stat card ─────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, highlight, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: highlight ? C.accent : C.white,
      border: `1px solid ${highlight ? C.accent : C.border}`,
      borderRadius: 12,
      padding: '20px 24px',
      minWidth: 160,
      height: 120,
      boxShadow: highlight ? '0 4px 16px rgba(13,71,161,0.25)' : C.shadow,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      cursor: onClick ? 'pointer' : 'default',
      flexShrink: 0,
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,71,161,0.18)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = highlight ? '0 4px 16px rgba(13,71,161,0.25)' : C.shadow;
    }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: highlight ? 'rgba(255,255,255,0.75)' : C.textMuted,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontFamily: FONT,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: 26,
        fontWeight: 700,
        color: highlight ? '#fff' : C.text,
        fontFamily: FONT,
        lineHeight: 1.2,
      }}
    >
      {value ?? '—'}
    </span>
    {sub && (
      <span
        style={{
          fontSize: 11,
          color: highlight ? 'rgba(255,255,255,0.6)' : C.textMuted,
          fontFamily: FONT,
        }}
      >
        {sub}
      </span>
    )}
    {highlight && <div style={{ height: 3, width: 40, background: C.warning, borderRadius: 2 }} />}
  </div>
);

/* ─── Mini clickable stat row (used for My CRM Leads: Fresh / Pending) ────── */
const MiniStatRow = ({ icon, label, value, sub, color, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 14px',
      background: color + '0D',
      border: `1px solid ${color}30`,
      borderRadius: 10,
      cursor: 'pointer',
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateX(4px)';
      e.currentTarget.style.boxShadow = C.shadow;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateX(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: color + '1A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 17 } })}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT }}>{label}</div>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT }}>{sub}</div>
      </div>
    </div>
    <span style={{ fontSize: 19, fontWeight: 800, color, fontFamily: FONT, flexShrink: 0 }}>{value}</span>
  </div>
);

/* ─── Stage card ────────────────────────────────────────────────────────── */
const ManagerStageCard = ({ stage, displayName, icon, leadsCount, totalLeads, isNegative, onClick }) => {
  const color = isNegative ? C.danger : C.accent;
  const pct = totalLeads > 0 ? ((leadsCount / totalLeads) * 100).toFixed(1) : 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: isNegative ? C.danger : C.white,
        border: `1px solid ${isNegative ? C.danger : C.border}`,
        borderRadius: 10,
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
        boxShadow: isNegative ? '0 4px 16px rgba(240,68,56,0.28)' : C.shadow,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        minHeight: 70,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = isNegative
          ? '0 8px 24px rgba(240,68,56,0.38)'
          : C.shadowMd;
        if (!isNegative) e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isNegative ? '0 4px 16px rgba(240,68,56,0.28)' : C.shadow;
        e.currentTarget.style.borderColor = isNegative ? C.danger : C.border;
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: isNegative ? 'rgba(255,255,255,0.85)' : C.textMuted,
            fontFamily: FONT,
            marginBottom: 4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayName ?? stage.stageName}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              lineHeight: 1,
              color: isNegative ? '#fff' : color,
              fontFamily: FONT,
            }}
          >
            {leadsCount}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: isNegative ? 'rgba(255,255,255,0.7)' : C.textMuted,
              fontFamily: FONT,
            }}
          >
            {pct}%
          </span>
        </div>
      </div>
      <div style={{ flexShrink: 0, opacity: isNegative ? 0.9 : 0.55, display: 'flex', alignItems: 'center' }}>
        {React.cloneElement(icon, { sx: { color: isNegative ? '#fff' : color, fontSize: 22 } })}
      </div>
    </div>
  );
};

/* ─── Stage list row ────────────────────────────────────────────────────── */
const ManagerStageRow = ({ stage, displayName, icon, leadsCount, totalLeads, isNegative, onClick }) => {
  const color = isNegative ? C.danger : C.accent;
  const pct = totalLeads > 0 ? ((leadsCount / totalLeads) * 100).toFixed(1) : 0;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        cursor: 'pointer',
        marginBottom: 8,
        transition: 'transform 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(4px)';
        e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: color + '12',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 18 } })}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT, marginBottom: 5 }}>
          {displayName ?? stage.stageName}
        </div>
        <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: FONT }}>{leadsCount}</div>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT }}>{pct}%</div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════════════ */
export const DashboardManagerCRMDATA = ({ darkMode = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarW = useSidebarWidth();

  const [userlogin, setUserLogin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoadingProgress, setDataLoadingProgress] = useState(0);
  const [expandedTeamLeaders, setExpandedTeamLeaders] = useState({});

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) setUserLogin(user);
  }, []);

  // محاكاة تقدم التحميل الأولي
  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingProgress((oldProgress) => {
          if (oldProgress >= 90) {
            clearInterval(interval);
            return 90;
          }
          return Math.min(oldProgress + 10, 90);
        });
      }, 300);

      return () => clearInterval(interval);
    } else {
      setLoadingProgress(100);
      setTimeout(() => setLoadingProgress(0), 500);
    }
  }, [loading]);

  // محاكاة تقدم تحميل البيانات
  useEffect(() => {
    if (dataLoading) {
      const interval = setInterval(() => {
        setDataLoadingProgress((oldProgress) => {
          if (oldProgress >= 85) {
            clearInterval(interval);
            return 85;
          }
          return Math.min(oldProgress + 5, 85);
        });
      }, 200);

      return () => clearInterval(interval);
    } else {
      setDataLoadingProgress(100);
      setTimeout(() => {
        setDataLoadingProgress(0);
      }, 300);
    }
  }, [dataLoading]);

  // تعريف الأيقونات مع ألوان موحدة
  const stagesIcons = {
    Fresh: <TipsAndUpdatesIcon fontSize="large" />,
    'No Stage': <TipsAndUpdatesIcon fontSize="large" />,
    'No Action Done': <HighlightOffIcon fontSize="large" />,
    Pending: <PendingActionsIcon fontSize="large" />,
    Follow: <BookmarkIcon fontSize="large" />,
    'Follow Up': <BookmarkIcon fontSize="large" />,
    'Long Follow': <BookmarkIcon fontSize="large" />,
    Transfer: <BackspaceIcon fontSize="large" />,
    Interested: <ThumbUpIcon fontSize="large" />,
    'Not Interested': <BackspaceIcon fontSize="large" />,
    Meeting: <AccessTimeFilledIcon fontSize="large" />,
    'Following After Meeting': <WorkHistoryIcon fontSize="large" />,
    'Follow After Meeting': <WorkHistoryIcon fontSize="large" />,
    'Cancel Meeting': <CancelIcon fontSize="large" />,
    'Done Deal': <TaskAltIcon fontSize="large" />,
    'No Answer': <PhonelinkEraseIcon fontSize="large" />,
    'No Answer 1st Call': <AccessAlarmIcon fontSize="large" />,
    Cancellation: <CancelIcon fontSize="large" />,
    EOI: <TipsAndUpdatesIcon fontSize="large" />,
  };

  // ✅ استدعاء API المدير (CRM Data)
  const fetchDashboardData = async () => {
    try {
      setDataLoading(true);
      setDataLoadingProgress(10);

      if (userlogin?.data?.email) {
        await dispatch(Get_Dashboard_data_For_Mangers_crm_data(userlogin.data.email));

        setDataLoadingProgress(95);
        setTimeout(() => {
          setDataLoadingProgress(100);
          setTimeout(() => {
            setDataLoading(false);
            setDataLoadingProgress(0);
          }, 300);
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching manager CRM dashboard data:', error);
      setDataLoading(false);
      setDataLoadingProgress(0);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userlogin?.data?.email) {
      setLoading(true);
      fetchDashboardData();
    }
  }, [userlogin]);

  // جلب البيانات من Redux store
  const managerDataFromStore = useSelector((state) => state.Allusers?.DashboardForManagercrmdata);

  // استخراج البيانات حسب شكل الـ API
  const dashboardData = managerDataFromStore?.data || {};

  const managerInfo = dashboardData.managerInfo || {};
  const summary = dashboardData.summary || {};
  const dashboard = dashboardData.dashboard || [];

  // ⭐ الباك اند بقى بيرجع الحاجات دي في managerOwnStats / teamStats بدل ما
  // كانت flat على مستوى data مباشرة. بنعمل fallback للشكل القديم عشان أي
  // كاش قديم (30 ثانية TTL) لسه راجع بالشكل القديم مايكسرش الشاشة.
  const managerOwnStats = dashboardData.managerOwnStats || {};
  const teamStats = dashboardData.teamStats || {};

  const managerFresh =
    managerOwnStats.managerFresh ||
    dashboardData.managerFresh || { leadsCount: 0, stageId: null, salesIds: [] };
  const managerPending =
    managerOwnStats.managerPending ||
    dashboardData.managerPending || { leadsCount: 0, stageId: null, salesIds: [] };

  // ⭐ نصيب باقي التيم (مش المدير نفسه) في No Stage و Pending
  const noActionAdded = teamStats.noActionAdded || { leadsCount: 0, stageId: null, salesIds: [] };
  const teamPending = teamStats.teamPending || { leadsCount: 0, stageId: null, salesIds: [] };

  const teamLeaders = dashboardData.teamLeaders || [];
  const directManagerSales = dashboardData.directManagerSales || [];

  // إحصائيات
  const totalLeads = summary.totalLeads || 0;
  const totalTeamLeaders = summary.totalTeamLeaders || 0;
  const totalSales = summary.totalSales || 0;
  const managerFreshLeads = summary.managerFreshLeads || 0;
  const managerPendingLeads = summary.managerPendingLeads || 0;
  const noActionAddedLeads = summary.noActionAddedLeads || 0;
  const teamPendingLeads = summary.teamPendingLeads || 0;

  // تحديث حالة التحميل
  useEffect(() => {
    if (managerDataFromStore && !dataLoading) {
      setLoading(false);
    }
  }, [managerDataFromStore, dataLoading]);

  // عرض بيانات التصحيح
  useEffect(() => {
    if (managerDataFromStore && !loading) {
      console.log('📊 Manager CRM Dashboard data:', {
        managerInfo,
        summary,
        teamLeadersCount: teamLeaders.length,
        dashboardLength: dashboard.length,
        managerFresh,
        managerPending,
        noActionAdded,
        teamPending,
      });
    }
  }, [managerDataFromStore, loading]);

  // دالة لترتيب الـ Stages
  const getSortedStages = (stages) => {
    const order = [
      'No Stage',
      'No Action Done',
      'Pending',
      'Interested',
      'Follow Up',
      'Meeting',
      'Follow After Meeting',
      'EOI',
      'Done Deal',
      'Cancel Meeting',
      'No Answer',
      'Not Interested',
      'Transfer',
      'Long Follow',
      'Follow',
      'Following After Meeting',
      'No Answer 1st Call',
      'Cancellation',
      'Fresh',
    ];

    return [...stages].sort((a, b) => {
      const indexA = order.indexOf(a.stageName);
      const indexB = order.indexOf(b.stageName);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return 0;
    });
  };

  // إعداد جميع المراحل للعرض
  // المدير عنده نصيبه معروض لوحده في كارت "My CRM Leads" فوق (Fresh + Pending
  // الخاصين بيه بس)، فعشان الأرقام هنا متتكررش مع اللي فوق:
  //  - "No Stage" اتقسمت لكارتين: "Fresh" (نصيب المدير) و"No Action Done"
  //    (نصيب باقي التيم) — الأرقام جايه جاهزة من الباك اند.
  //  - "Pending" هنا بقت بتعرض نصيب باقي التيم بس (teamPending).
  const allStages = useMemo(() => {
    const otherStages = dashboard.filter(
      (stage) =>
        stage.stageName !== 'Fresh' &&
        stage.stageName !== 'No Stage' &&
        stage.stageName !== 'Pending',
    );

    const noStageEntry = dashboard.find((s) => s.stageName === 'No Stage');
    const pendingEntry = dashboard.find((s) => s.stageName === 'Pending');

    otherStages.push(
      {
        stageId: noStageEntry?.stageId ?? managerFresh.stageId ?? null,
        stageName: 'No Stage', // بيتحول لاسم "Fresh" وقت العرض عن طريق getDisplayStageName
        leadsCount: managerFresh.leadsCount || 0,
      },
      {
        stageId: noStageEntry?.stageId ?? noActionAdded.stageId ?? null,
        stageName: 'No Action Done',
        leadsCount: noActionAdded.leadsCount || 0,
      },
    );

    otherStages.push({
      stageId: pendingEntry?.stageId ?? teamPending.stageId ?? null,
      stageName: 'Pending',
      leadsCount: teamPending.leadsCount || 0,
    });

    return getSortedStages(otherStages);
  }, [
    dashboard,
    managerFresh.leadsCount,
    managerFresh.stageId,
    noActionAdded.leadsCount,
    noActionAdded.stageId,
    teamPending.leadsCount,
    teamPending.stageId,
  ]);

  // ✅ دالة التعامل مع النقر على مرحلة (للـ CRM data)
  const handleStageClick = async (stage) => {
    const { stageName, stageId } = stage;

    console.log('🎯 Stage clicked (CRM):', stageName, 'ID:', stageId);

    // تنظيف الـ localStorage من الفلاتر القديمة
    localStorage.removeItem('stagestorage');
    localStorage.removeItem('filterForSales');
    localStorage.removeItem('managerFilter');
    localStorage.removeItem('managerFreshFilter');
    localStorage.removeItem('managerPendingFilter');

    // "Fresh" كارت المراحل بيمثل ليدز الـ No Stage الخاصة بالمدير نفسه بالظبط
    // زي كارت "My CRM Leads > Fresh"، فبنستخدم نفس سلوك handleManagerFreshClick
    if (stageName === 'No Stage') {
      handleManagerFreshClick();
      return;
    }

    // "No Action Done" (نصيب التيم في No Stage) ليها دالة خاصة تحت عشان
    // الليدز اللي هتظهر تبقى بالظبط نفس الرقم المعروض في الكارت.
    if (stageName === 'No Action Done') {
      handleNoActionAddedClick();
      return;
    }

    // باقي الكروت (من ضمنهم "Pending" بتاعة التيم) بتتعامل زي أي كارت تاني
    // بالظبط: بتدور بالـ stage الحقيقية من غير أي فلتر خاص بسيلز معين.
    const effectiveStageName = stageName;

    // حفظ الفلتر الجديد
    localStorage.setItem('stagestorage', effectiveStageName);

    if (stageId) {
      navigate(
        `/AllLeadsManagercrmdata?stage=${encodeURIComponent(effectiveStageName)}&stageId=${stageId}`,
      );
    } else {
      navigate(`/AllLeadsManagercrmdata?stage=${encodeURIComponent(effectiveStageName)}&noStage=true`);
    }
  };

  // ✅ دالة النقر على Manager Fresh (CRM)
  const handleManagerFreshClick = () => {
    console.log('🍃 Manager Fresh (CRM) clicked');

    localStorage.removeItem('stagestorage');
    localStorage.removeItem('filterForSales');
    localStorage.removeItem('managerFilter');

    localStorage.setItem('stagestorage', 'No Stage');
    localStorage.setItem('managerFreshFilter', 'true');

    // ⚠️ تصليح: كان بيقرا managerPending.salesIds غلط هنا (بحجة "نفس الـ sale")،
    // والباك اند بقى بيرجع managerFresh.salesIds بشكل مستقل، فبنستخدمها هي.
    const salesId =
      managerFresh.salesIds && managerFresh.salesIds.length > 0
        ? managerFresh.salesIds[0]
        : null;

    if (salesId) {
      navigate(
        `/AllLeadsManagercrmdata?stage=No Stage&noStage=true&salesId=${salesId}&managerFresh=true`,
      );
      console.log('🚀 Navigating to Fresh leads (CRM) with salesId:', salesId);
    } else {
      console.warn('⚠️ No salesId found for manager fresh (CRM)');
      navigate(`/AllLeadsManagercrmdata?stage=No Stage&noStage=true&managerFresh=true`);
    }
  };

  // ✅ دالة النقر على "No Action Added" (نصيب باقي التيم في No Stage، CRM)
  // بتفلتر بنفس الشرطين المستخدمين في حساب العدد بالظبط: stage = No Stage
  // + نفس sales التيم (مش سيلز المدير)، عشان الليدز اللي هتظهر تطابق الرقم
  // المعروض في الكارت.
  const handleNoActionAddedClick = () => {
    console.log('📋 No Action Added (CRM) clicked');

    localStorage.removeItem('stagestorage');
    localStorage.removeItem('filterForSales');
    localStorage.removeItem('managerFilter');
    localStorage.removeItem('managerFreshFilter');
    localStorage.removeItem('managerPendingFilter');

    localStorage.setItem('stagestorage', 'No Stage');
    localStorage.setItem('noActionAddedFilter', 'true');

    const stageId = noActionAdded.stageId;
    const salesIdsParam =
      noActionAdded.salesIds && noActionAdded.salesIds.length > 0
        ? noActionAdded.salesIds.join(',')
        : null;

    if (salesIdsParam) {
      const stageParam = stageId ? `&stageId=${stageId}` : '';
      navigate(
        `/AllLeadsManagercrmdata?stage=No Stage${stageParam}&sales=${salesIdsParam}&noActionAdded=true`,
      );
      console.log('🚀 Navigating to No Action Added leads (CRM) with sales:', salesIdsParam);
    } else {
      console.warn('⚠️ No team sales found for No Action Added (CRM)');
      navigate(`/AllLeadsManagercrmdata?stage=No Stage&noStage=true&noActionAdded=true`);
    }
  };

  // ✅ دالة النقر على Manager Pending (CRM)
  const handleManagerPendingClick = () => {
    console.log('⏳ Manager Pending (CRM) clicked');

    localStorage.removeItem('stagestorage');
    localStorage.removeItem('filterForSales');
    localStorage.removeItem('managerFilter');

    localStorage.setItem('stagestorage', 'Pending');
    localStorage.setItem('managerPendingFilter', 'true');

    const salesId =
      managerPending.salesIds && managerPending.salesIds.length > 0
        ? managerPending.salesIds[0]
        : null;

    const pendingStageId = managerPending.stageId;

    if (salesId && pendingStageId) {
      navigate(
        `/AllLeadsManagercrmdata?stage=Pending&stageId=${pendingStageId}&salesId=${salesId}&managerPending=true`,
      );
      console.log(
        '🚀 Navigating to Pending leads (CRM) with salesId:',
        salesId,
        'and stageId:',
        pendingStageId,
      );
    } else if (salesId) {
      navigate(`/AllLeadsManagercrmdata?stage=Pending&salesId=${salesId}&managerPending=true`);
      console.log('🚀 Navigating to Pending leads (CRM) with salesId:', salesId);
    } else {
      console.warn('⚠️ No salesId found for manager pending (CRM)');
      navigate(`/AllLeadsManagercrmdata?stage=Pending&managerPending=true`);
    }
  };

  // دالة النقر على Team Leader (CRM)
  const handleTeamLeaderClick = (teamLeader) => {
    console.log('👥 Team Leader clicked (CRM):', teamLeader.teamLeaderInfo);

    localStorage.setItem('selectedTeamLeader', JSON.stringify(teamLeader.teamLeaderInfo));
    localStorage.setItem('teamLeaderFilter', 'true');

    navigate(`/AllLeadsManagercrmdata?teamLeaderId=${teamLeader.teamLeaderInfo.id}`);
  };

  // دالة النقر على Sales (CRM)
  const handleSalesClick = (sale) => {
    console.log('👤 Sales clicked (CRM):', sale.name);

    localStorage.setItem('selectedSales', JSON.stringify(sale));
    localStorage.setItem('salesFilter', 'true');

    navigate(`/AllLeadsManagercrmdata?salesId=${sale.id}`);
  };

  // دالة toggle expansion للـ Team Leader
  const toggleTeamLeaderExpand = (teamLeaderId) => {
    setExpandedTeamLeaders((prev) => ({
      ...prev,
      [teamLeaderId]: !prev[teamLeaderId],
    }));
  };

  // فلترة Team Leaders - استبعاد اللي هو نفسه المدير
  const filteredTeamLeaders = useMemo(() => {
    return teamLeaders.filter((tl) => tl.teamLeaderInfo.email !== managerInfo.email);
  }, [teamLeaders, managerInfo.email]);

  const isNegativeStage = (name) =>
    name === 'Done Deal' || name === 'Not Interested' || name === 'Cancel Meeting';

  // بيانات الرسم البياني
  const getStageChartData = useMemo(() => {
    const labels = allStages.map((stage) => getDisplayStageName(stage.stageName));
    const data = allStages.map((stage) => stage.leadsCount);

    const backgroundColors = allStages.map((stage) =>
      isNegativeStage(stage.stageName) ? C.danger : C.accent,
    );

    return {
      labels: labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: C.white,
          borderWidth: 2,
          hoverOffset: 12,
        },
      ],
    };
  }, [allStages]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: C.text,
          font: { size: 11, family: FONT },
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: C.white,
        titleColor: C.text,
        bodyColor: C.textMuted,
        borderColor: C.border,
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} leads (${percentage}%)`;
          },
        },
      },
    },
    cutout: '62%',
  };

  // عرض حالة التحميل الرئيسية
  if (loading && !managerDataFromStore) {
    return (
      <div
        style={{
          marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
          marginTop: 64,
          minHeight: 'calc(100vh - 64px)',
          background: C.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: `3px solid ${C.border}`,
              borderTopColor: C.accent,
              margin: '0 auto',
              animation: 'crmSpin 0.8s linear infinite',
            }}
          />
          <p style={{ marginTop: 14, color: C.textMuted, fontFamily: FONT, fontSize: 13.5 }}>
            Loading manager CRM dashboard...
          </p>
        </div>
        <style>{`@keyframes crmSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* ── render ── */
  return (
    <div
      style={{
        marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
        marginTop: 64,
        minHeight: 'calc(100vh - 64px)',
        background: C.bg,
        fontFamily: FONT,
        position: 'relative',
        transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {/* طبقة تحميل البيانات */}
      {dataLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(28,35,64,0.55)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9997,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              width: '90%',
              maxWidth: 380,
              textAlign: 'center',
              background: C.white,
              borderRadius: 14,
              padding: '32px 28px',
              boxShadow: C.shadowMd,
            }}
          >
            <DashboardIcon sx={{ fontSize: 34, color: C.accent, mb: 1 }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: FONT }}>
              Loading Manager CRM Dashboard
            </div>
            <div style={{ fontSize: 12.5, color: C.textMuted, fontFamily: FONT, marginTop: 4, marginBottom: 18 }}>
              Fetching CRM team and leads data...
            </div>
            <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${dataLoadingProgress}%`,
                  height: '100%',
                  background: C.accent,
                  borderRadius: 3,
                  transition: 'width 0.3s linear',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ fontSize: 11.5, color: C.textMuted, fontFamily: FONT }}>
                {dataLoadingProgress < 30
                  ? 'Loading manager...'
                  : dataLoadingProgress < 60
                    ? 'Loading team leaders...'
                    : dataLoadingProgress < 90
                      ? 'Loading CRM leads...'
                      : 'Almost done...'}
              </span>
              <span style={{ fontSize: 11.5, color: C.accent, fontWeight: 700, fontFamily: FONT }}>
                {Math.round(dataLoadingProgress)}%
              </span>
            </div>
          </div>
        </div>
      )}

      <Container fluid style={{ padding: '28px 24px 48px', maxWidth: '100%', opacity: dataLoading ? 0.6 : 1, transition: 'opacity 0.3s ease' }}>
        {/* ── Header ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 28,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 34,
                fontWeight: 800,
                color: C.text,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                fontFamily: FONT,
              }}
            >
              Manager CRM<br />Dashboard
            </div>
            <div style={{ fontSize: 13.5, color: C.textMuted, marginTop: 8, fontFamily: FONT }}>
              Welcome back, <strong style={{ color: C.text }}>{managerInfo.name || userlogin?.data?.name}</strong>
              {' '}— manage your CRM team leaders and track overall performance.
            </div>
            {managerDataFromStore?.meta && (
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 4, fontFamily: FONT }}>
                Data loaded in {managerDataFromStore.meta.executionTime} •{' '}
                {managerDataFromStore.meta.fromCache ? 'Cached' : 'Fresh'}
              </div>
            )}
          </div>

          {/* Stat cards */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <StatCard label="Total CRM Leads" value={totalLeads} sub="All team leaders" onClick={() => navigate('/AllLeadsManagercrmdata')} />
            <StatCard label="Team Leaders" value={filteredTeamLeaders.length} sub={`Managing ${totalSales} sales`} />
            <StatCard label="Total Sales" value={totalSales} sub="All team leaders" />
          </div>
        </div>

        {/* ── Toolbar row ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShowChartIcon sx={{ color: C.accent, fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: FONT }}>Quick Overview (CRM)</span>
          </div>
          <Link to="/LeadsForManagers" style={{ textDecoration: 'none' }}>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: C.white,
                color: C.text,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = C.accent;
                e.currentTarget.style.color = C.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.text;
              }}
            >
              <ContactMailIcon sx={{ fontSize: 16 }} />
              Main Data
            </button>
          </Link>
        </div>

        {/* ── My CRM Leads (Fresh / Pending) ── */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: C.shadow,
            padding: '18px 20px',
            marginBottom: 28,
            maxWidth: 420,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: FONT, marginBottom: 12 }}>
            My CRM Leads
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MiniStatRow
              icon={<TipsAndUpdatesIcon />}
              label="Fresh (CRM)"
              sub="No Stage leads"
              value={managerFresh.leadsCount}
              color={C.success}
              onClick={handleManagerFreshClick}
            />
            <MiniStatRow
              icon={<PendingActionsIcon />}
              label="Pending (CRM)"
              sub="Pending stage leads"
              value={managerPending.leadsCount}
              color={C.warning}
              onClick={handleManagerPendingClick}
            />
          </div>
        </div>

        {/* ── Team Leaders (CRM) ── */}
        {filteredTeamLeaders.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <GroupsIcon sx={{ color: C.accent, fontSize: 18 }} />
              <span style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: FONT }}>
                Team Leaders (CRM) ({filteredTeamLeaders.length})
              </span>
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginBottom: 16 }}>
              Click on any team leader to view their CRM details, or expand to see their sales
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredTeamLeaders.map((teamLeader) => (
                <div
                  key={teamLeader.teamLeaderInfo.id}
                  style={{
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    boxShadow: C.shadow,
                    overflow: 'hidden',
                  }}
                >
                  {/* Team Leader Header */}
                  <div
                    style={{
                      padding: '16px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: expandedTeamLeaders[teamLeader.teamLeaderInfo.id]
                        ? `1px solid ${C.border}`
                        : 'none',
                      transition: 'background 0.15s',
                    }}
                    onClick={() => toggleTeamLeaderExpand(teamLeader.teamLeaderInfo.id)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.accentSoft)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: C.success + '15',
                          border: `1px solid ${C.success}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <SupervisorAccountIcon sx={{ color: C.success, fontSize: 22 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: C.text, fontFamily: FONT, marginBottom: 3 }}>
                          {teamLeader.teamLeaderInfo.name}
                        </div>
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11.5, color: C.textMuted, fontFamily: FONT }}>
                            <strong style={{ color: C.text }}>Sales:</strong> {teamLeader.salesCount}
                          </span>
                          <span style={{ fontSize: 11.5, color: C.textMuted, fontFamily: FONT }}>
                            <strong style={{ color: C.text }}>Total:</strong> {teamLeader.leads?.total || 0}
                          </span>
                          <span style={{ fontSize: 11.5, color: C.textMuted, fontFamily: FONT }}>
                            <strong style={{ color: C.text }}>Fresh:</strong> {teamLeader.leads?.fresh || 0}
                          </span>
                          <span style={{ fontSize: 11.5, color: C.textMuted, fontFamily: FONT }}>
                            <strong style={{ color: C.text }}>Pending:</strong> {teamLeader.leads?.pending || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    {expandedTeamLeaders[teamLeader.teamLeaderInfo.id] ? (
                      <ExpandLessIcon sx={{ color: C.accent }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: C.accent }} />
                    )}
                  </div>

                  {/* Sales تحت Team Leader */}
                  <Collapse in={expandedTeamLeaders[teamLeader.teamLeaderInfo.id]}>
                    <div style={{ padding: '18px 20px', background: C.bg }}>
                      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginBottom: 12 }}>
                        Sales under {teamLeader.teamLeaderInfo.name} (CRM):
                      </div>
                      <Row>
                        {teamLeader.sales.map((sale) => (
                          <Col key={sale.id} xs={12} md={6} lg={4} className="mb-3">
                            <div
                              onClick={() => handleSalesClick(sale)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '12px 14px',
                                background: C.white,
                                border: `1px solid ${C.border}`,
                                borderRadius: 10,
                                cursor: 'pointer',
                                transition: 'transform 0.15s, border-color 0.15s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.borderColor = C.warning;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.borderColor = C.border;
                              }}
                            >
                              <div
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 8,
                                  background: C.warning + '15',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <AssignmentIndIcon sx={{ color: C.warning, fontSize: 18 }} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT }}>
                                  {sale.name}
                                </div>
                                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: FONT }}>
                                  {sale.userlog?.name || 'No user'}
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Collapse>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Stage cards grid (CRM) ── */}
        {allStages.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <BookmarkIcon sx={{ color: C.accent, fontSize: 18 }} />
              <span style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: FONT }}>
                Stages Overview (CRM) ({allStages.length})
              </span>
              <span style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT }}>
                — click any stage to view its CRM leads
              </span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 12,
                marginTop: 16,
              }}
            >
              {allStages.map((stage) => (
                <ManagerStageCard
                  key={`${stage.stageId ?? stage.stageName}-${stage.stageName}`}
                  stage={stage}
                  displayName={getDisplayStageName(stage.stageName)}
                  icon={stagesIcons[stage.stageName] || <AccessibilityNewIcon />}
                  leadsCount={stage.leadsCount}
                  totalLeads={totalLeads}
                  isNegative={isNegativeStage(stage.stageName)}
                  onClick={() => handleStageClick(stage)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Chart + List row (CRM) ── */}
        {allStages.length > 0 && (
          <div className="mgr-crm-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
            {/* Doughnut */}
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden' }}>
              <div style={{ padding: '20px 22px 16px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT }}>
                  <BarChartIcon sx={{ fontSize: 17, color: C.accent, verticalAlign: 'middle', mr: 0.5 }} />
                  CRM Leads Distribution by Stage
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginTop: 3 }}>
                  Visual breakdown of CRM leads across all stages
                </div>
              </div>
              <div style={{ padding: '20px 22px', height: 350 }}>
                <Doughnut data={getStageChartData} options={chartOptions} />
              </div>
            </div>

            {/* Stages list */}
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden' }}>
              <div style={{ padding: '20px 22px 16px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT }}>
                  <BookmarkIcon sx={{ fontSize: 17, color: C.accent, verticalAlign: 'middle', mr: 0.5 }} />
                  Stages Overview (CRM)
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginTop: 3 }}>
                  Click any stage to view its CRM leads
                </div>
              </div>
              <div style={{ padding: '16px 22px', maxHeight: 350, overflowY: 'auto' }}>
                {allStages.map((stage) => (
                  <ManagerStageRow
                    key={`${stage.stageId ?? stage.stageName}-${stage.stageName}`}
                    stage={stage}
                    displayName={getDisplayStageName(stage.stageName)}
                    icon={stagesIcons[stage.stageName] || <AccessibilityNewIcon />}
                    leadsCount={stage.leadsCount}
                    totalLeads={totalLeads}
                    isNegative={isNegativeStage(stage.stageName)}
                    onClick={() => handleStageClick(stage)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── قسم الأداء ── */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: C.shadow,
            padding: '24px 22px',
          }}
        >
          <DashbordPerformanceForAllUsers darkMode={darkMode} />
        </div>
      </Container>

      <style>{`
        @media (max-width: 900px) {
          .mgr-crm-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};
